import React, { useMemo } from "react";
import { useT, Namespace } from "@racoon/i18n";
import { cn, Column, DataTable, Hyperlink } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useTableSettings } from "~/shared/hooks/useTableSettings";
import { getEnumName } from "@racoon/lib";
import { compareIds } from "~/shared/lib/compare-ids";

// Figma "Job_type Status" chip: green when active, grey otherwise.
const StatusChip = ({ active, children }: { active: boolean; children: React.ReactNode }) => (
  <span
    className={cn(
      "inline-flex h-6 w-[100px] items-center justify-center whitespace-nowrap rounded-lg border px-3 py-1 text-xs font-medium leading-4 tracking-[-0.01em]",
      active
        ? "border-green-500 bg-green-100 text-green-600"
        : "border-slate-300 bg-slate-200 text-text-subtle",
    )}
  >
    {children}
  </span>
);

// DataTable left-aligns every cell, while the design centres these columns.
const Centered = ({ children }: { children: React.ReactNode }) => (
  <div className="flex w-full justify-center">{children}</div>
);

interface DepotTableProps {
  depots: ConnectProto.Location[];
  isLoading: boolean;
  selectedDepot?: ConnectProto.Location | null;
  onRowSelect?: (depot: ConnectProto.Location | null) => void;
  onDepotEdit?: (depot: ConnectProto.Location) => void;
  /** Global disposal sites: the status column toggles the site's operator instead. */
  activation?: {
    activeOperatorIds: Set<string>;
    onToggle: (operatorId: bigint) => void;
    disabled: boolean;
  };
}

export const DepotTable: React.FC<DepotTableProps> = ({
  depots,
  isLoading,
  selectedDepot,
  onRowSelect,
  onDepotEdit,
  activation,
}) => {
  const t = useT();
  const { selectedCompanyId } = useSelectedCompany();
  const { columnConfig, handleColumnConfigChange } = useTableSettings(
    "depots",
    selectedCompanyId || undefined,
  );

  const columns = useMemo<Column<ConnectProto.Location>[]>(() => {
    return [
      {
        id: "id",
        header: "ID",
        accessor: (row) => String(row.id),
        sortable: true,
        sortFn: (a, b) => compareIds(a.id, b.id),
        width: 77,
        cell: (info) => <Centered>{info.getValue() as string}</Centered>,
      },
      {
        id: "name",
        header: t(Namespace.Settings, "depot.table.location"),
        key: "name",
        sortable: true,
        width: 193,
        cell: (info) =>
          onDepotEdit ? (
            <Hyperlink onOpen={() => onDepotEdit(info.row.original)}>
              {(info.getValue() as string) || "-"}
            </Hyperlink>
          ) : (
            (info.getValue() as string) || "-"
          ),
      },
      {
        id: "type",
        header: t(Namespace.Settings, "depot.table.locationType"),
        accessor: (row) => {
          const name = getEnumName(ConnectProto.LocationTypeSchema, row.type);
          return name ? t(Namespace.Enum, name) : "-";
        },
        sortable: true,
        width: 292,
      },
      {
        id: "status",
        header: t(Namespace.Settings, "depot.table.activated"),
        accessor: (row) =>
          activation
            ? activation.activeOperatorIds.has(String(row.ownerOrganizationId))
            : row.status === ConnectProto.LocationStatus.ACTIVE,
        sortable: true,
        width: 160,
        cell: (info) => {
          const active = info.getValue() as boolean;
          const label = t(
            Namespace.Settings,
            active ? "depot.status.activated" : "depot.status.notActivated",
          );
          if (!activation)
            return (
              <Centered>
                <StatusChip active={active}>{label}</StatusChip>
              </Centered>
            );
          return (
            <Centered>
              <button
                type="button"
                disabled={activation.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  activation.onToggle(info.row.original.ownerOrganizationId);
                }}
                className="disabled:cursor-wait"
              >
                <StatusChip active={active}>{label}</StatusChip>
              </button>
            </Centered>
          );
        },
      },
      {
        id: "gisLink",
        header: t(Namespace.Settings, "depot.table.geolocation"),
        accessor: (row) => row.gisLink,
        cell: (info) => {
          const href = info.getValue() as string | undefined;
          // Global sites belong to other organizations: only plain web links are rendered.
          const safeHref = href && /^https?:\/\//i.test(href) ? href : undefined;
          return (
            <Centered>
              {safeHref ? (
                <Hyperlink href={safeHref}>{t(Namespace.Settings, "depot.link")}</Hyperlink>
              ) : (
                "-"
              )}
            </Centered>
          );
        },
      },
    ];
  }, [t, onDepotEdit, activation]);

  if (depots.length === 0 && !isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
        <p className="text-gray-500">{t(Namespace.Settings, "depot.table.empty")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-hidden">
      <DataTable
        className="h-full !rounded-lg"
        columns={columns}
        data={depots}
        loading={isLoading}
        rowKey={(row) => row.id}
        selectedRowKeys={selectedDepot ? [selectedDepot.id] : []}
        onRowClick={
          onRowSelect ? (row) => onRowSelect(selectedDepot?.id === row.id ? null : row) : undefined
        }
        columnConfig={columnConfig}
        onColumnConfigChange={handleColumnConfigChange}
        namespace={Namespace.Settings}
      />
    </div>
  );
};
